import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import VNCConsole from './VNCConsole';
import NodeDetailsModal from './NodeDetailsModal';

import {
    LogOut,
    Server,
    Users,
    Settings,
    Plus,
    Edit,
    Trash2,
    ArrowLeft,
    Monitor,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Terminal
} from 'lucide-react';
import axios from 'axios';

// Auto-refresh intervals for admin panel (in milliseconds)
const ADMIN_VMS_REFRESH_INTERVAL = 20000; // 20 seconds for all VMs (more frequent for admin)
const ADMIN_REQUESTS_REFRESH_INTERVAL = 30000; // 30 seconds for VM requests
const ADMIN_NODES_REFRESH_INTERVAL = 60000; // 1 minute for nodes
const ADMIN_USERS_REFRESH_INTERVAL = 120000; // 2 minutes for users (less frequent)
const ADMIN_ASSIGNMENTS_REFRESH_INTERVAL = 90000; // 1.5 minutes for assignments

// Função para gerar senha aleatória
const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
};

function AdminPanel() {
    const { user, logout } = useAuth();
    const [activeTab, setActiveTab] = useState('nodes');
    const [nodes, setNodes] = useState([]);
    const [users, setUsers] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [vmRequests, setVmRequests] = useState([]);
    const [allVMs, setAllVMs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [showConsoleModal, setShowConsoleModal] = useState(false);
    const [consoleData, setConsoleData] = useState(null);
    const [autoRefreshing, setAutoRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(new Date());


    useEffect(() => {
        fetchData();

        // Set up auto-refresh interval based on active tab
        let interval;

        if (activeTab === 'all-vms') {
            interval = setInterval(() => fetchData(true), ADMIN_VMS_REFRESH_INTERVAL);
        } else if (activeTab === 'vm-requests') {
            interval = setInterval(() => fetchData(true), ADMIN_REQUESTS_REFRESH_INTERVAL);
        } else if (activeTab === 'nodes') {
            interval = setInterval(() => fetchData(true), ADMIN_NODES_REFRESH_INTERVAL);
        } else if (activeTab === 'users') {
            interval = setInterval(() => fetchData(true), ADMIN_USERS_REFRESH_INTERVAL);
        } else if (activeTab === 'assignments') {
            interval = setInterval(() => fetchData(true), ADMIN_ASSIGNMENTS_REFRESH_INTERVAL);
        }

        // Cleanup interval on tab change or component unmount
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [activeTab]);

    const fetchData = async (isAutoRefresh = false) => {
        if (isAutoRefresh) {
            setAutoRefreshing(true);
        } else {
            setLoading(true);
        }

        try {
            if (activeTab === 'nodes') {
                const response = await axios.get('/api/admin/nodes');
                setNodes(response.data);
            } else if (activeTab === 'users') {
                const response = await axios.get('/api/admin/users');
                setUsers(response.data);
            } else if (activeTab === 'assignments') {
                const [assignmentsRes, nodesRes, usersRes] = await Promise.all([
                    axios.get('/api/admin/assignments'),
                    axios.get('/api/admin/nodes'),
                    axios.get('/api/admin/users')
                ]);
                setAssignments(assignmentsRes.data);
                setNodes(nodesRes.data);
                setUsers(usersRes.data);
            } else if (activeTab === 'vm-requests') {
                const response = await axios.get('/api/vm-requests');
                setVmRequests(response.data);
            } else if (activeTab === 'all-vms') {
                setUsers((await axios.get('/api/admin/users')).data);
                const response = await axios.get('/api/admin/all-vms');
                setAllVMs(response.data);
            }
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
        } finally {
            setLoading(false);
            setAutoRefreshing(false);
            setLastRefresh(new Date());
        }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm('Tem certeza que deseja excluir este item?')) return;

        try {
            await axios.delete(`/api/admin/${type}/${id}`);
            fetchData();
        } catch (error) {
            console.error('Erro ao excluir:', error);
            alert('Erro ao excluir item');
        }
    };

    const openModal = (type, item = null) => {
        setModalType(type);
        setEditingItem(item);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setModalType('');
        setEditingItem(null);
    };

    const handleApproveRequest = async (id, admin_notes = '', node_id = null, vmid = null, price = null) => {
        try {
            await axios.put(`/api/admin/vm-requests/${id}/approve`, { 
                admin_notes, 
                node_id, 
                vmid,
                price 
            });
            fetchData();
        } catch (error) {
            console.error('Erro ao aprovar solicitação:', error);
            alert('Erro ao aprovar solicitação: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleRejectRequest = async (id, admin_notes = '') => {
        try {
            await axios.put(`/api/admin/vm-requests/${id}/reject`, { admin_notes });
            fetchData();
        } catch (error) {
            console.error('Erro ao rejeitar solicitação:', error);
            alert('Erro ao rejeitar solicitação');
        }
    };

    const handleAssignVM = async (vmid, nodeId, userId, realNodeName, sendToBilling = false, price = null, observations = null) => {
        try {
            const response = await axios.post('/api/admin/assignments', {
                userId: userId,
                nodeId: nodeId,
                vmid: vmid,
                realNodeName: realNodeName,
                sendToBilling: sendToBilling,
                price: price,
                observations: observations
            });
            alert(response.data.message || 'VM atribuída com sucesso!');
            fetchData(); // Refresh data
        } catch (error) {
            console.error('Erro ao atribuir VM:', error);
            alert('Erro ao atribuir VM: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleUnassignVM = async (vmid, nodeId) => {
        if (!window.confirm('Tem certeza que deseja desvincular esta VM? O usuário perderá o acesso a ela.')) {
            return;
        }

        try {
            // Buscar todas as atribuições para encontrar a correta
            const assignmentsResponse = await axios.get('/api/admin/assignments');
            const allAssignments = assignmentsResponse.data;

            // Encontrar o assignment ID baseado no vmid e nodeId
            const assignment = allAssignments.find(a => a.vmid == vmid && a.node_id == nodeId);
            if (assignment) {
                await axios.delete(`/api/admin/assignments/${assignment.id}`);
                alert('VM desvinculada com sucesso!');
                fetchData(); // Refresh data
            } else {
                alert('Erro: Atribuição não encontrada');
            }
        } catch (error) {
            console.error('Erro ao desvincular VM:', error);
            alert('Erro ao desvincular VM: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleConsoleAccess = (vm) => {
        setConsoleData(vm);
        setShowConsoleModal(true);
    };



    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4 sm:py-6">
                        <div className="flex items-center">
                            <div class="w-32 mx-auto sm:w-40 rounded-full">
                                <img src="https://repo.telic.net.br/imgdir/Tglobal_360x100px_2.png" />
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            <a
                                href="/dashboard"
                                className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 border border-gray-300 text-xs sm:text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                                <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                                <span className="hidden sm:inline">Voltar para o Dashboard</span>
                            </a>
                            <span className="text-xs sm:text-sm text-gray-700 hidden md:block">Admin: {user.email}</span>
                            <button
                                onClick={logout}
                                className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 border border-transparent text-xs sm:text-sm leading-4 font-medium rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300"
                            >
                                <LogOut className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                                <span className="hidden sm:inline">Sair</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                {/* Tabs */}
                <div className="px-4 sm:px-0">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto">
                            <button
                                onClick={() => setActiveTab('nodes')}
                                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'nodes'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <Server className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                                <span className="hidden sm:inline">Nós Proxmox</span>
                                <span className="sm:hidden">Nós</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('users')}
                                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'users'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <Users className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                                Usuários
                            </button>
                            <button
                                onClick={() => setActiveTab('assignments')}
                                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'assignments'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <Monitor className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                                <span className="hidden sm:inline">Atribuições</span>
                                <span className="sm:hidden">VMs</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('vm-requests')}
                                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'vm-requests'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <Clock className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                                <span className="hidden sm:inline">Solicitações</span>
                                <span className="sm:hidden">Pedidos</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('all-vms')}
                                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'all-vms'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <Server className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                                <span className="hidden sm:inline">Todas as VMs</span>
                                <span className="sm:hidden">Todas</span>
                            </button>
                        </nav>
                    </div>
                </div>

                {/* Auto-refresh indicator */}
                <div className="px-4 sm:px-0 mb-4">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center space-x-2">
                            {autoRefreshing && (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500 mr-1"></div>
                                    <span>Atualizando...</span>
                                </div>
                            )}
                            {!autoRefreshing && (
                                <div className="flex items-center">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                                    <span>Atualização automática ativa</span>
                                </div>
                            )}
                        </div>
                        <div>
                            Última atualização: {lastRefresh.toLocaleTimeString('pt-BR')}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="px-4 py-6 sm:px-0">
                    {loading ? (
                        <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'nodes' && <NodesTab nodes={nodes} onEdit={(item) => openModal('node', item)} onDelete={(id) => handleDelete('nodes', id)} onAdd={() => openModal('node')} />}
                            {activeTab === 'users' && <UsersTab users={users} onEdit={(item) => openModal('user', item)} onDelete={(id) => handleDelete('users', id)} onAdd={() => openModal('user')} />}
                            {activeTab === 'assignments' && <AssignmentsTab assignments={assignments} nodes={nodes} users={users} onDelete={(id) => handleDelete('assignments', id)} onAdd={() => openModal('assignment')} />}
                            {activeTab === 'vm-requests' && <VMRequestsTab vmRequests={vmRequests} onApprove={handleApproveRequest} onReject={handleRejectRequest} onDelete={(id) => handleDelete('vm-requests', id)} />}
                            {activeTab === 'all-vms' && <AllVMsTab allVMs={allVMs} users={users} onAssignVM={handleAssignVM} onUnassignVM={handleUnassignVM} onConsoleAccess={handleConsoleAccess} />}
                        </>
                    )}
                </div>
            </main>

            {/* Modal */}
            {showModal && (
                <Modal
                    type={modalType}
                    item={editingItem}
                    nodes={nodes}
                    users={users}
                    onClose={closeModal}
                    onSave={() => {
                        closeModal();
                        fetchData();
                    }}
                />
            )}

            {/* VNC Console */}
            {showConsoleModal && consoleData && (
                <VNCConsole
                    nodeId={consoleData.nodeId}
                    vmid={consoleData.vmid}
                    vmType={consoleData.type}
                    node={consoleData.node}
                    vmName={consoleData.name}
                    onClose={() => {
                        setShowConsoleModal(false);
                        setConsoleData(null);
                    }}
                />
            )}


        </div>
    );
}

// Nodes Tab Component
function NodesTab({ nodes, onEdit, onDelete, onAdd }) {
    const [selectedNodeId, setSelectedNodeId] = useState(null);

    const handleViewDetails = (nodeId) => {
        setSelectedNodeId(nodeId);
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Nós Proxmox</h2>
                <button
                    onClick={onAdd}
                    className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Nó
                </button>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                    {nodes.map((node) => (
                        <li key={node.id} className="px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900">{node.name}</h3>
                                    <p className="text-sm text-gray-500">{node.host}:{node.port}</p>
                                    <p className="text-sm text-gray-500">{node.username ? 'Autenticação via User' : 'Autenticação via Token'}</p>
                                    {node.backup_storage && (
                                        <p className="text-sm text-gray-500">Storage de Backup: {node.backup_storage}</p>
                                    )}
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => handleViewDetails(node.id)}
                                        className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                                    >
                                        <Server className="h-4 w-4 mr-1" />
                                        Detalhes
                                    </button>

                                    <button
                                        onClick={() => onEdit(node)}
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        <Edit className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(node.id)}
                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Node Details Modal */}
            {selectedNodeId && (
                <NodeDetailsModal
                    nodeId={selectedNodeId}
                    onClose={() => setSelectedNodeId(null)}
                />
            )}
        </div>
    );
}

// Users Tab Component
function UsersTab({ users, onEdit, onDelete, onAdd }) {
    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Usuários</h2>
                <button
                    onClick={onAdd}
                    className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Usuário
                </button>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                    {users.map((user) => (
                        <li key={user.id} className="px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900">{user.full_name}</h3>
                                    <p className="text-sm text-gray-500">Email: {user.email}</p>
                                    <p className="text-sm text-gray-500">
                                        {user.person_type === 'juridica' ? 'CNPJ' : 'CPF'}: {user.cpf_cnpj || 'Não informado'}
                                    </p>
                                    <p className="text-sm text-gray-500">Telefone: {user.phone || 'Não informado'}</p>
                                    <div className="flex items-center space-x-4 mt-1">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {user.role === 'admin' ? 'Administrador' : 'Cliente'}
                                        </span>
                                        {user.temp_password ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                Senha Temporária
                                            </span>
                                        ):''}
                                        {user.must_change_password ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                Deve Alterar Senha
                                            </span>
                                        ):''}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Criado em: {new Date(user.created_at).toLocaleDateString('pt-BR')}</p>
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => onDelete(user.id)}
                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

// Assignments Tab Component
function AssignmentsTab({ assignments, nodes, users, onDelete, onAdd }) {
    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Atribuições de VMs</h2>
                <button
                    onClick={onAdd}
                    className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Atribuição
                </button>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                    {assignments.map((assignment) => (
                        <li key={assignment.id} className="px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900">
                                        {assignment.vm_name ? `${assignment.vm_name} (ID: ${assignment.vmid})` : `VM ${assignment.vmid}`}
                                    </h3>
                                    <p className="text-sm text-gray-500">Usuário: {assignment.full_name} [{assignment.email}]</p>
                                    <p className="text-sm text-gray-500">Cluster: {assignment.node_name} ({assignment.host})</p>
                                    {assignment.real_node_name && (
                                        <p className="text-sm text-gray-500">Nó: {assignment.real_node_name}</p>
                                    )}
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => onDelete(assignment.id)}
                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}



// VM Request Configuration Modal Component
function VMRequestConfigModal({ onClose, onSave }) {
    const [activeConfigTab, setActiveConfigTab] = useState('os');
    const [loading, setLoading] = useState(false);
    const [availableOS, setAvailableOS] = useState([]);
    const [pricing, setPricing] = useState({
        cpu_price_per_core: 0,
        ram_price_per_gb: 0,
        storage_price_per_gb: 0,
        base_price: 0
    });
    const [newOS, setNewOS] = useState({ name: '', display_name: '', iso_url: '', is_active: true });
    const [editingOS, setEditingOS] = useState(null);

    useEffect(() => {
        fetchConfigData();
    }, []);

    const fetchConfigData = async () => {
        try {
            setLoading(true);
            const [osResponse, pricingResponse] = await Promise.all([
                axios.get('/api/admin/vm-request-config/os'),
                axios.get('/api/admin/vm-request-config/pricing')
            ]);
            
            setAvailableOS(osResponse.data);
            setPricing(pricingResponse.data);
        } catch (error) {
            console.error('Erro ao carregar configurações:', error);
            alert('Erro ao carregar configurações');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveOS = async (osData) => {
        try {
            if (editingOS) {
                await axios.put(`/api/admin/vm-request-config/os/${editingOS.id}`, osData);
            } else {
                await axios.post('/api/admin/vm-request-config/os', osData);
            }
            setNewOS({ name: '', display_name: '', iso_url: '', is_active: true });
            setEditingOS(null);
            fetchConfigData();
        } catch (error) {
            console.error('Erro ao salvar SO:', error);
            alert('Erro ao salvar sistema operacional');
        }
    };

    const handleDeleteOS = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir este sistema operacional?')) return;
        
        try {
            await axios.delete(`/api/admin/vm-request-config/os/${id}`);
            fetchConfigData();
        } catch (error) {
            console.error('Erro ao excluir SO:', error);
            alert('Erro ao excluir sistema operacional');
        }
    };



    const handleSavePricing = async () => {
        try {
            await axios.put('/api/admin/vm-request-config/pricing', pricing);
            alert('Valores atualizados com sucesso!');
        } catch (error) {
            console.error('Erro ao salvar valores:', error);
            alert('Erro ao salvar configurações de valores');
        }
    };

    const startEditOS = (os) => {
        setEditingOS(os);
        setNewOS({ ...os });
    };

    const cancelEdit = () => {
        setEditingOS(null);
        setNewOS({ name: '', display_name: '', iso_url: '', is_active: true });
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-md bg-white m-4">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900">
                        Configurações de Solicitações de VM
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                    >
                        ×
                    </button>
                </div>

                {/* Config Tabs */}
                <div className="border-b border-gray-200 mb-6">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveConfigTab('os')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeConfigTab === 'os'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Sistemas Operacionais
                        </button>

                        <button
                            onClick={() => setActiveConfigTab('pricing')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeConfigTab === 'pricing'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Valores
                        </button>
                    </nav>
                </div>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <>
                        {/* Operating Systems Tab */}
                        {activeConfigTab === 'os' && (
                            <div className="space-y-6">
                                <div className="bg-gray-50 p-4 rounded-md">
                                    <h4 className="text-lg font-medium text-gray-900 mb-4">
                                        {editingOS ? 'Editar Sistema Operacional' : 'Adicionar Sistema Operacional'}
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Nome Interno</label>
                                            <input
                                                type="text"
                                                value={newOS.name}
                                                onChange={(e) => setNewOS({ ...newOS, name: e.target.value })}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="ubuntu-22.04"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Nome de Exibição</label>
                                            <input
                                                type="text"
                                                value={newOS.display_name}
                                                onChange={(e) => setNewOS({ ...newOS, display_name: e.target.value })}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Ubuntu 22.04 LTS"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700">URL da ISO (opcional)</label>
                                            <input
                                                type="url"
                                                value={newOS.iso_url || ''}
                                                onChange={(e) => setNewOS({ ...newOS, iso_url: e.target.value })}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="https://exemplo.com/ubuntu-22.04.iso"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={newOS.is_active}
                                                onChange={(e) => setNewOS({ ...newOS, is_active: e.target.checked })}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label className="ml-2 block text-sm text-gray-900">Ativo</label>
                                        </div>
                                    </div>
                                    <div className="flex space-x-3 mt-4">
                                        <button
                                            onClick={() => handleSaveOS(newOS)}
                                            disabled={!newOS.name || !newOS.display_name}
                                            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            {editingOS ? 'Atualizar' : 'Adicionar'}
                                        </button>
                                        {editingOS && (
                                            <button
                                                onClick={cancelEdit}
                                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                            >
                                                Cancelar
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                                    <ul className="divide-y divide-gray-200">
                                        {availableOS.map((os) => (
                                            <li key={os.id} className="px-6 py-4">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h3 className="text-lg font-medium text-gray-900">{os.display_name}</h3>
                                                        <p className="text-sm text-gray-500">Nome interno: {os.name}</p>
                                                        {os.iso_url && (
                                                            <p className="text-sm text-gray-500">ISO: {os.iso_url}</p>
                                                        )}
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                            os.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                            {os.is_active ? 'Ativo' : 'Inativo'}
                                                        </span>
                                                    </div>
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => startEditOS(os)}
                                                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteOS(os.id)}
                                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}



                        {/* Pricing Tab */}
                        {activeConfigTab === 'pricing' && (
                            <div className="space-y-6">
                                <div className="bg-gray-50 p-6 rounded-md">
                                    <h4 className="text-lg font-medium text-gray-900 mb-4">Configuração de Valores</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Valor Base (R$)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={pricing.base_price}
                                                onChange={(e) => setPricing({ ...pricing, base_price: parseFloat(e.target.value) || 0 })}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Valor por VCPU (R$)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={pricing.cpu_price_per_core}
                                                onChange={(e) => setPricing({ ...pricing, cpu_price_per_core: parseFloat(e.target.value) || 0 })}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Valor por GB de RAM (R$)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={pricing.ram_price_per_gb}
                                                onChange={(e) => setPricing({ ...pricing, ram_price_per_gb: parseFloat(e.target.value) || 0 })}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Valor por GB de Armazenamento (R$)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={pricing.storage_price_per_gb}
                                                onChange={(e) => setPricing({ ...pricing, storage_price_per_gb: parseFloat(e.target.value) || 0 })}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleSavePricing}
                                        className="mt-6 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                                    >
                                        Salvar Valores
                                    </button>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-md">
                                    <h5 className="text-md font-medium text-blue-900 mb-2">Exemplo de Cálculo</h5>
                                    <div className="text-sm text-blue-800 space-y-1">
                                        <div>VM com 2 cores, 4GB RAM, 50GB armazenamento:</div>
                                        <div>• Taxa base: R$ {pricing.base_price}</div>
                                        <div>• CPU (2 cores): R$ {(2 * pricing.cpu_price_per_core).toFixed(2)}</div>
                                        <div>• RAM (4 GB): R$ {(4 * pricing.ram_price_per_gb).toFixed(2)}</div>
                                        <div>• Armazenamento (50 GB): R$ {(50 * pricing.storage_price_per_gb).toFixed(2)}</div>
                                        <div className="border-t border-blue-200 pt-2 font-medium">
                                            Total mensal: R$ {(
                                                parseFloat(pricing.base_price) + 
                                                (2 * pricing.cpu_price_per_core) + 
                                                (4 * pricing.ram_price_per_gb) + 
                                                (50 * pricing.storage_price_per_gb)
                                            ).toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AdminPanel;// Modal Component
function Modal({ type, item, nodes, users, onClose, onSave }) {
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [realNodes, setRealNodes] = useState([]);
    const [availableVMs, setAvailableVMs] = useState([]);
    const [loadingRealNodes, setLoadingRealNodes] = useState(false);
    const [loadingVMs, setLoadingVMs] = useState(false);

    const [testingConnection, setTestingConnection] = useState(false);
    const [connectionResult, setConnectionResult] = useState(null);
    const [availableStorages, setAvailableStorages] = useState([]);
    const [loadingStorages, setLoadingStorages] = useState(false);

    useEffect(() => {
        if (item) {
            // Map the item data correctly for editing
            const mappedData = { ...item };
            if (type === 'node') {
                // Map database field names to form field names
                mappedData.tokenId = item.token_id;
                mappedData.tokenSecret = item.token_secret;
                mappedData.backupStorage = item.backup_storage;
            }
            setFormData(mappedData);

            // If editing a node, load available storages
            if (type === 'node' && item.host) {
                loadAvailableStoragesForEdit(item);
            }
        } else {
            setFormData({});
        }
    }, [item, type]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (type === 'node') {
                const nodeData = {
                    name: formData.name,
                    host: formData.host,
                    port: formData.port || 8006,
                    username: formData.username,
                    password: formData.password,
                    tokenId: formData.tokenId,
                    tokenSecret: formData.tokenSecret,
                    backupStorage: formData.backupStorage
                };

                if (item) {
                    await axios.put(`/api/admin/nodes/${item.id}`, nodeData);
                } else {
                    await axios.post('/api/admin/nodes', nodeData);
                }
            } else if (type === 'user') {
                try{
                    const cadastro_ret = await axios.post('/api/admin/users', formData);
                    if(!cadastro_ret.data.mail_sent){
                        alert(`Erro ao enviar e-mail para "${formData['email']}". Exclua o usuário e tente novamente.`);
                    }
                } catch(e){
                    console.error(e);
                    if(e.response.data){
                        alert(`ERRO: ${e.response.data.error}. Tente novamente.`);
                    }
                }
            } else if (type === 'assignment') {
                const assignmentData = {
                    userId: formData.userId,
                    nodeId: formData.nodeId,
                    vmid: formData.vmid,
                    realNodeName: formData.realNodeName,
                    sendToBilling: formData.sendToBilling || false,
                    price: formData.sendToBilling ? formData.price : null,
                    observations: formData.sendToBilling ? formData.observations : null
                };
                await axios.post('/api/admin/assignments', assignmentData);
            }

            onSave();
        } catch (error) {
            console.error('Erro ao salvar:', error);
            alert(`Erro ao salvar: ${error.response?.data?.error || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = async (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // When cluster (nodeId) is selected, load real nodes
        if (name === 'nodeId' && value && type === 'assignment') {
            await loadRealNodes(value);
        }

        // When real node is selected, load VMs
        if (name === 'realNodeName' && value && formData.nodeId && type === 'assignment') {
            await loadVMs(formData.nodeId, value);
        }
    };

    const loadRealNodes = async (nodeId) => {
        try {
            setLoadingRealNodes(true);
            const response = await axios.get(`/api/admin/nodes/${nodeId}/real-nodes`);
            setRealNodes(response.data);
        } catch (error) {
            console.error('Error loading real nodes:', error);
            setRealNodes([]);
        } finally {
            setLoadingRealNodes(false);
        }
    };

    const loadVMs = async (nodeId, realNodeName) => {
        try {
            setLoadingVMs(true);
            // Get all VMs from the cluster and filter by real node
            const response = await axios.get('/api/admin/all-vms');
            const allVMs = response.data;

            // Filter VMs that are on the selected real node and cluster
            const filteredVMs = allVMs.filter(vm =>
                vm.nodeId == nodeId &&
                vm.realNodeName === realNodeName &&
                !vm.assigned // Only show unassigned VMs
            );

            setAvailableVMs(filteredVMs);
        } catch (error) {
            console.error('Error loading VMs:', error);
            setAvailableVMs([]);
        } finally {
            setLoadingVMs(false);
        }
    };

    const testConnection = async () => {
        if (!formData.host) {
            alert('Por favor, preencha o host antes de testar a conexão');
            return;
        }

        setTestingConnection(true);
        setConnectionResult(null);

        try {
            const response = await axios.post('/api/admin/test-connection', {
                host: formData.host,
                port: formData.port || 8006,
                username: formData.username,
                password: formData.password,
                tokenId: formData.tokenId,
                tokenSecret: formData.tokenSecret
            });

            setConnectionResult({
                success: true,
                message: response.data.message,
                vmCount: response.data.vmCount,
                vms: response.data.vms
            });

            // If connection is successful, load available storages
            if (!item && type === 'node') {
                await loadAvailableStorages();
            }


        } catch (error) {
            setConnectionResult({
                success: false,
                message: error.response?.data?.error || 'Erro ao testar conexão',
                details: error.response?.data?.details
            });
        } finally {
            setTestingConnection(false);
        }
    };

    const loadAvailableStorages = async () => {
        try {
            setLoadingStorages(true);
            const response = await axios.post('/api/admin/test-connection/storages', {
                host: formData.host,
                port: formData.port || 8006,
                username: formData.username,
                password: formData.password,
                tokenId: formData.tokenId,
                tokenSecret: formData.tokenSecret
            });

            setAvailableStorages(response.data.storages || []);
        } catch (error) {
            console.error('Error loading storages:', error);
            setAvailableStorages([]);
        } finally {
            setLoadingStorages(false);
        }
    };

    const loadAvailableStoragesForEdit = async (nodeItem) => {
        try {
            setLoadingStorages(true);
            const response = await axios.post('/api/admin/test-connection/storages', {
                host: nodeItem.host,
                port: nodeItem.port || 8006,
                username: nodeItem.username,
                password: nodeItem.password,
                tokenId: nodeItem.token_id,
                tokenSecret: nodeItem.token_secret
            });

            setAvailableStorages(response.data.storages || []);
        } catch (error) {
            console.error('Error loading storages for edit:', error);
            setAvailableStorages([]);
        } finally {
            setLoadingStorages(false);
        }
    };



    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white m-4">
                <div className="mt-3">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        {item ? 'Editar' : 'Adicionar'} {type === 'node' ? 'Nó' : type === 'user' ? 'Usuário' : 'Atribuição'}
                    </h3>

                    {item && type === 'node' && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <p className="text-sm text-blue-800">
                                <strong>Dica:</strong> Ao editar, você pode deixar os campos de senha e token vazios para manter os valores atuais.
                            </p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {type === 'node' && (
                            <>
                                {/* Seção: Informações Básicas */}
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                        <Server className="h-4 w-4 mr-2" />
                                        Informações Básicas
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Nome visível</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name || ''}
                                                onChange={handleChange}
                                                required
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Nome do cluster/nó"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Porta</label>
                                            <input
                                                type="number"
                                                name="port"
                                                value={formData.port || 8006}
                                                onChange={handleChange}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700">Host/IP</label>
                                        <input
                                            type="text"
                                            name="host"
                                            value={formData.host || ''}
                                            onChange={handleChange}
                                            required
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="IP ou hostname do Proxmox"
                                        />
                                    </div>
                                </div>
                                {/* Seção: Credenciais de Acesso */}
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                        <Settings className="h-4 w-4 mr-2" />
                                        Credenciais de Acesso
                                    </h4>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Usuário</label>
                                                <input
                                                    type="text"
                                                    name="username"
                                                    value={formData.username || ''}
                                                    onChange={handleChange}
                                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder="Ex: root@pam"
                                                />
                                                {item && formData.username && (
                                                    <p className="text-xs text-green-600 mt-1">
                                                        ✓ Usuário atual: {formData.username}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Senha
                                                    {item && <span className="text-xs text-gray-500 ml-1">(deixe vazio para manter)</span>}
                                                </label>
                                                <input
                                                    type="password"
                                                    name="password"
                                                    value={formData.password || ''}
                                                    onChange={handleChange}
                                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder={item ? "Deixe vazio para manter atual" : "Senha do usuário"}
                                                />
                                            </div>
                                        </div>

                                        <div className="border-t pt-4">
                                            <p className="text-sm text-gray-600 mb-3">
                                                <strong>Alternativa:</strong> Use Token API em vez de usuário/senha
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Token ID
                                                        {item && <span className="text-xs text-gray-500 ml-1">(deixe vazio para manter)</span>}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="tokenId"
                                                        value={formData.tokenId || ''}
                                                        onChange={handleChange}
                                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder={item ? "Deixe vazio para manter atual" : "Ex: mytoken"}
                                                    />
                                                    {item && formData.tokenId && (
                                                        <p className="text-xs text-green-600 mt-1">
                                                            ✓ Token ID atual: {formData.tokenId}
                                                        </p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Token Secret
                                                        {item && <span className="text-xs text-gray-500 ml-1">(deixe vazio para manter)</span>}
                                                    </label>
                                                    <input
                                                        type="password"
                                                        name="tokenSecret"
                                                        value={formData.tokenSecret || ''}
                                                        onChange={handleChange}
                                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder={item ? "Deixe vazio para manter atual" : "Secret do token"}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Seção: Configurações */}
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                        <Monitor className="h-4 w-4 mr-2" />
                                        Configurações
                                    </h4>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Storage de Backup</label>
                                        {availableStorages.length > 0 ? (
                                            <select
                                                name="backupStorage"
                                                value={formData.backupStorage || ''}
                                                onChange={handleChange}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="">Selecione um storage de backup</option>
                                                {availableStorages.map(storage => (
                                                    <option key={storage.storage} value={storage.storage}>
                                                        {storage.storage} ({storage.type}) - Nós: {storage.nodes.join(', ')}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                name="backupStorage"
                                                value={formData.backupStorage || ''}
                                                onChange={handleChange}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Ex: local, backup-storage"
                                            />
                                        )}
                                        {loadingStorages && (
                                            <div className="flex items-center mt-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                                                <span className="text-sm text-gray-600">Carregando storages...</span>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between mt-2">
                                            <p className="text-xs text-gray-500">
                                                {availableStorages.length > 0
                                                    ? 'Selecione o storage que será usado para backups das VMs'
                                                    : 'Teste a conexão primeiro para carregar os storages disponíveis'
                                                }
                                            </p>
                                            {(formData.host && !loadingStorages) && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const dataToUse = {
                                                            host: formData.host,
                                                            port: formData.port || 8006,
                                                            username: formData.username,
                                                            password: formData.password,
                                                            token_id: formData.tokenId,
                                                            token_secret: formData.tokenSecret
                                                        };
                                                        loadAvailableStoragesForEdit(dataToUse);
                                                    }}
                                                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                                                >
                                                    Recarregar Storages
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Seção: Teste de Conexão */}
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Teste de Conexão
                                    </h4>
                                    <div className="space-y-3">
                                        <button
                                            type="button"
                                            onClick={testConnection}
                                            disabled={testingConnection}
                                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            {testingConnection ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                                            ) : (
                                                <Server className="h-4 w-4 mr-2" />
                                            )}
                                            {testingConnection ? 'Testando...' : 'Testar Conexão'}
                                        </button>

                                        {/* Connection Result */}
                                        {connectionResult && (
                                            <div className={`p-3 rounded-md ${connectionResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                                <div className="flex">
                                                    <div className="flex-shrink-0">
                                                        {connectionResult.success ? (
                                                            <CheckCircle className="h-5 w-5 text-green-400" />
                                                        ) : (
                                                            <XCircle className="h-5 w-5 text-red-400" />
                                                        )}
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className={`text-sm font-medium ${connectionResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                                            {connectionResult.message}
                                                        </p>
                                                        {connectionResult.success && connectionResult.vmCount !== undefined && (
                                                            <p className="text-sm text-green-700 mt-1">
                                                                {connectionResult.vmCount} VMs encontradas
                                                            </p>
                                                        )}
                                                        {connectionResult.vms && connectionResult.vms.length > 0 && (
                                                            <div className="mt-2">
                                                                <p className="text-xs text-green-600">Primeiras VMs:</p>
                                                                <ul className="text-xs text-green-600 ml-2">
                                                                    {connectionResult.vms.map(vm => (
                                                                        <li key={vm.vmid}>VM {vm.vmid}: {vm.name} ({vm.status})</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        {connectionResult.details && (
                                                            <p className="text-sm text-red-700 mt-1">
                                                                {connectionResult.details}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </>
                        )}

                        {type === 'user' && (
                            <>
                                {/* Seção: Informações Básicas */}
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                        <Users className="h-4 w-4 mr-2" />
                                        Informações Básicas
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Tipo de Pessoa *</label>
                                            <select
                                                name="person_type"
                                                value={formData.person_type || 'fisica'}
                                                onChange={handleChange}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="fisica">Pessoa Física</option>
                                                <option value="juridica">Pessoa Jurídica</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Função</label>
                                            <select
                                                name="role"
                                                value={formData.role || 'client'}
                                                onChange={handleChange}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="client">Cliente</option>
                                                <option value="admin">Administrador</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700">Nome Completo *</label>
                                        <input
                                            type="text"
                                            name="full_name"
                                            value={formData.full_name || ''}
                                            onChange={handleChange}
                                            required
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder={formData.person_type === 'juridica' ? 'Razão Social' : 'Nome completo'}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                {formData.person_type === 'juridica' ? 'CNPJ *' : 'CPF *'}
                                            </label>
                                            <input
                                                type="text"
                                                name="cpf_cnpj"
                                                value={formData.cpf_cnpj || ''}
                                                onChange={handleChange}
                                                required
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder={formData.person_type === 'juridica' ? '00.000.000/0000-00' : '000.000.000-00'}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Telefone *</label>
                                            <input
                                                type="text"
                                                name="phone"
                                                value={formData.phone || ''}
                                                onChange={handleChange}
                                                required
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="(00) 00000-0000"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700">E-mail *</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email || ''}
                                            onChange={handleChange}
                                            required
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="usuario@exemplo.com"
                                        />
                                    </div>
                                </div>

                                {/* Seção: Credenciais de Acesso */}
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                                        <Settings className="h-4 w-4 mr-2" />
                                        Credenciais de Acesso
                                    </h4>
                                    <div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Senha *</label>
                                            <div className="mt-1">
                                                <input
                                                    type="text"
                                                    name="password"
                                                    value={formData.password || ''}
                                                    onChange={handleChange}
                                                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder="Digite a senha ou clique em 'Gerar Senha'"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const randomPassword = generateRandomPassword();
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            password: randomPassword
                                                        }));
                                                    }}
                                                    className="mt-2 w-full px-4 py-2 text-sm font-medium rounded-md border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100"
                                                >
                                                    🎲 Gerar Senha Aleatória
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                O usuário deverá alterar a senha no primeiro login
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {type === 'assignment' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Usuário</label>
                                    <select
                                        name="userId"
                                        value={formData.userId || ''}
                                        onChange={handleChange}
                                        required
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">Selecione um usuário</option>
                                        {users.filter(u => u.role === 'client').map(user => (
                                            <option key={user.id} value={user.id}>{user.full_name} [{user.email}]</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Cluster</label>
                                    <select
                                        name="nodeId"
                                        value={formData.nodeId || ''}
                                        onChange={handleChange}
                                        required
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">Selecione um cluster</option>
                                        {nodes.map(node => (
                                            <option key={node.id} value={node.id}>{node.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nó Real</label>
                                    {loadingRealNodes ? (
                                        <div className="mt-1 flex items-center justify-center py-2 border border-gray-300 rounded-md">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                                            <span className="text-sm text-gray-500">Carregando nós...</span>
                                        </div>
                                    ) : (
                                        <select
                                            name="realNodeName"
                                            value={formData.realNodeName || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={!formData.nodeId}
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        >
                                            <option value="">Selecione o nó real</option>
                                            {realNodes.map(node => (
                                                <option key={node.name} value={node.name}>
                                                    {node.name} ({node.status})
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    {!formData.nodeId && (
                                        <p className="mt-1 text-xs text-gray-500">Selecione um cluster primeiro</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">VM</label>
                                    {loadingVMs ? (
                                        <div className="mt-1 flex items-center justify-center py-2 border border-gray-300 rounded-md">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                                            <span className="text-sm text-gray-500">Carregando VMs...</span>
                                        </div>
                                    ) : (
                                        <select
                                            name="vmid"
                                            value={formData.vmid || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={!formData.realNodeName}
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        >
                                            <option value="">Selecione uma VM</option>
                                            {availableVMs.map(vm => (
                                                <option key={vm.vmid} value={vm.vmid}>
                                                    {vm.name ? `${vm.name} (ID: ${vm.vmid})` : `VM ${vm.vmid}`} - {vm.status}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    {!formData.realNodeName && (
                                        <p className="mt-1 text-xs text-gray-500">Selecione um nó real primeiro</p>
                                    )}
                                    {formData.realNodeName && availableVMs.length === 0 && !loadingVMs && (
                                        <p className="mt-1 text-xs text-yellow-600">Nenhuma VM não atribuída encontrada neste nó</p>
                                    )}
                                </div>

                                {/* Seção de Faturamento */}
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="flex items-center mb-3">
                                        <input
                                            type="checkbox"
                                            name="sendToBilling"
                                            checked={formData.sendToBilling || false}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                sendToBilling: e.target.checked
                                            })}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label className="ml-2 block text-sm font-medium text-gray-700">
                                            Enviar ao financeiro?
                                        </label>
                                    </div>
                                    
                                    {formData.sendToBilling && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Valor (R$)</label>
                                                <input
                                                    type="number"
                                                    name="price"
                                                    value={formData.price || ''}
                                                    onChange={handleChange}
                                                    step="0.01"
                                                    min="0"
                                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder="0,00"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Observações (opcional)</label>
                                                <textarea
                                                    name="observations"
                                                    value={formData.observations || ''}
                                                    onChange={handleChange}
                                                    rows={3}
                                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder="Observações adicionais sobre a VM..."
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                            >
                                {loading ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}// VM Requests Tab Component
function VMRequestsTab({ vmRequests, onApprove, onReject, onDelete }) {
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [actionType, setActionType] = useState('');
    const [selectedCluster, setSelectedCluster] = useState('');
    const [selectedVM, setSelectedVM] = useState('');
    const [price, setPrice] = useState('');
    const [clusters, setClusters] = useState([]);
    const [availableVMs, setAvailableVMs] = useState([]);
    const [loadingVMs, setLoadingVMs] = useState(false);

    const handleAction = (request, action) => {
        setSelectedRequest(request);
        setActionType(action);
        setAdminNotes('');
        
        if (action === 'approve') {
            setSelectedCluster('');
            setSelectedVM('');
            setPrice(request.estimated_price ? parseFloat(request.estimated_price).toFixed(2) : '');
            setAvailableVMs([]);
            setShowApprovalModal(true);
            fetchClusters();
        } else {
            setShowNotesModal(true);
        }
    };

    const fetchClusters = async () => {
        try {
            const response = await axios.get('/api/admin/nodes');
            setClusters(response.data);
        } catch (error) {
            console.error('Erro ao buscar clusters:', error);
        }
    };

    const handleClusterChange = async (clusterId) => {
        setSelectedCluster(clusterId);
        setSelectedVM('');
        setAvailableVMs([]);
        
        if (clusterId) {
            setLoadingVMs(true);
            try {
                const response = await axios.get(`/api/admin/nodes/${clusterId}/vms`);
                setAvailableVMs(response.data);
            } catch (error) {
                console.error('Erro ao buscar VMs do cluster:', error);
                alert('Erro ao buscar VMs do cluster');
            } finally {
                setLoadingVMs(false);
            }
        }
    };

    const confirmAction = () => {
        if (actionType === 'approve') {
            if (!selectedCluster || !selectedVM) {
                alert('Por favor, selecione um cluster e uma VM');
                return;
            }
            onApprove(selectedRequest.id, adminNotes, selectedCluster, selectedVM, price);
            setShowApprovalModal(false);
        } else if (actionType === 'reject') {
            onReject(selectedRequest.id, adminNotes);
            setShowNotesModal(false);
        }
        setSelectedRequest(null);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'approved': return 'bg-green-100 text-green-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            case 'created': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'pending': return 'Pendente';
            case 'approved': return 'Aprovada';
            case 'rejected': return 'Rejeitada';
            case 'created': return 'Criada';
            default: return 'Desconhecido';
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Solicitações de VMs</h2>
                <button
                    onClick={() => setShowConfigModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                    <Settings className="h-4 w-4 mr-2" />
                    Configurações
                </button>
            </div>

            {vmRequests.length === 0 ? (
                <div className="text-center py-12">
                    <Clock className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma solicitação encontrada</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        As solicitações de VM dos clientes aparecerão aqui.
                    </p>
                </div>
            ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    <ul className="divide-y divide-gray-200">
                        {vmRequests.map((request) => (
                            <li key={request.id} className="px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-lg font-medium text-gray-900">{request.vm_name}</h3>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                                {getStatusText(request.status)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600">
                                            Solicitado por: <strong>{request.full_name}</strong> [{request.email}]
                                        </p>
                                        <div className="mt-2 grid grid-cols-4 gap-4 text-sm text-gray-600">
                                            <div>CPU: {request.cpu_cores} cores</div>
                                            <div>RAM: {request.memory_gb} GB</div>
                                            <div>Disco: {request.disk_gb} GB</div>
                                            <div>Região: {request.region_name || 'N/A'}</div>
                                        </div>
                                        {request.estimated_price > 0 && (
                                            <p className="text-sm text-gray-600">
                                                <strong>Valor Estimado:</strong> R$ {parseFloat(request.estimated_price).toFixed(2)}/mês
                                            </p>
                                        )}
                                        {request.os_template && (
                                            <p className="text-sm text-gray-600">
                                                <strong>Template:</strong> {request.os_template}
                                            </p>
                                        )}
                                        {request.description && (
                                            <p className="mt-2 text-sm text-gray-600">
                                                <strong>Descrição:</strong> {request.description}
                                            </p>
                                        )}
                                        {request.admin_notes && (
                                            <div className="mt-2 p-2 bg-gray-50 rounded">
                                                <p className="text-sm text-gray-700">
                                                    <strong>Observações do Admin:</strong> {request.admin_notes}
                                                </p>
                                            </div>
                                        )}
                                        <p className="mt-2 text-xs text-gray-500">
                                            Solicitado em: {new Date(request.created_at).toLocaleString('pt-BR')}
                                        </p>
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                        {request.status === 'pending' && (
                                            <>
                                                <button
                                                    onClick={() => handleAction(request, 'approve')}
                                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                                >
                                                    <CheckCircle className="h-4 w-4 mr-1" />
                                                    Aprovar
                                                </button>
                                                <button
                                                    onClick={() => handleAction(request, 'reject')}
                                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                                                >
                                                    <XCircle className="h-4 w-4 mr-1" />
                                                    Rejeitar
                                                </button>
                                            </>
                                        )}
                                        <button
                                            onClick={() => onDelete(request.id)}
                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Notes Modal (for rejection) */}
            {showNotesModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Rejeitar Solicitação
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                VM: <strong>{selectedRequest?.vm_name}</strong>
                            </p>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Observações (opcional)
                                </label>
                                <textarea
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    rows={3}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Adicione observações sobre esta decisão..."
                                />
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowNotesModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmAction}
                                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                                >
                                    Rejeitar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Approval Modal (for approval with cluster and VM selection) */}
            {showApprovalModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Aprovar Solicitação de VM
                            </h3>
                            
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                                <h4 className="font-medium text-blue-900 mb-2">Detalhes da Solicitação:</h4>
                                <div className="grid grid-cols-2 gap-4 text-sm text-blue-800">
                                    <div><strong>VM:</strong> {selectedRequest?.vm_name}</div>
                                    <div><strong>Cliente:</strong> {selectedRequest?.full_name}</div>
                                    <div><strong>CPU:</strong> {selectedRequest?.cpu_cores} cores</div>
                                    <div><strong>RAM:</strong> {selectedRequest?.memory_gb} GB</div>
                                    <div><strong>Disco:</strong> {selectedRequest?.disk_gb} GB</div>
                                    <div><strong>Template:</strong> {selectedRequest?.os_template || 'N/A'}</div>
                                </div>
                                {selectedRequest?.estimated_price > 0 && (
                                    <div className="mt-2 text-sm text-blue-800">
                                        <strong>Valor Estimado:</strong> R$ {parseFloat(selectedRequest.estimated_price).toFixed(2)}/mês
                                    </div>
                                )}
                                {selectedRequest?.os_template === 'custom' && selectedRequest?.custom_os_url && (
                                    <div className="mt-2 text-sm text-blue-800">
                                        <strong>URL da ISO Personalizada:</strong> 
                                        <a href={selectedRequest.custom_os_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:text-blue-800 underline break-all">
                                            {selectedRequest.custom_os_url}
                                        </a>
                                    </div>
                                )}
                                {selectedRequest?.description && (
                                    <div className="mt-2 text-sm text-blue-800">
                                        <strong>Descrição:</strong> {selectedRequest.description}
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Selecionar Cluster *
                                    </label>
                                    <select
                                        value={selectedCluster}
                                        onChange={(e) => handleClusterChange(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="">Selecione um cluster</option>
                                        {clusters.map(cluster => (
                                            <option key={cluster.id} value={cluster.id}>
                                                {cluster.name} ({cluster.host})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Selecionar VM *
                                    </label>
                                    <select
                                        value={selectedVM}
                                        onChange={(e) => setSelectedVM(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        disabled={!selectedCluster || loadingVMs}
                                        required
                                    >
                                        <option value="">
                                            {loadingVMs ? 'Carregando VMs...' : 'Selecione uma VM'}
                                        </option>
                                        {availableVMs.map(vm => (
                                            <option key={vm.vmid} value={vm.vmid}>
                                                {vm.name} (ID: {vm.vmid}) - {vm.node} - {vm.status}
                                            </option>
                                        ))}
                                    </select>
                                    {loadingVMs && (
                                        <div className="mt-1 text-xs text-gray-500 flex items-center">
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500 mr-1"></div>
                                            Buscando VMs do cluster...
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selectedVM && availableVMs.length > 0 && (
                                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                                    {(() => {
                                        const vm = availableVMs.find(v => v.vmid == selectedVM);
                                        return vm ? (
                                            <div>
                                                <h4 className="font-medium text-green-900 mb-2">VM Selecionada:</h4>
                                                <div className="grid grid-cols-2 gap-2 text-sm text-green-800">
                                                    <div><strong>Nome:</strong> {vm.name}</div>
                                                    <div><strong>ID:</strong> {vm.vmid}</div>
                                                    <div><strong>Nó:</strong> {vm.node}</div>
                                                    <div><strong>Status:</strong> {vm.status}</div>
                                                    {vm.maxcpu && <div><strong>CPU:</strong> {vm.maxcpu} cores</div>}
                                                    {vm.maxmem && <div><strong>RAM:</strong> {Math.round(vm.maxmem / 1024 / 1024 / 1024)} GB</div>}
                                                </div>
                                            </div>
                                        ) : null;
                                    })()}
                                </div>
                            )}

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Valor (opcional)
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2 text-gray-500">R$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={price}
                                        onChange={(e) => setPrice(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="0,00"
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Observações (opcional)
                                </label>
                                <textarea
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    rows={3}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Adicione observações sobre esta aprovação..."
                                />
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowApprovalModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmAction}
                                    disabled={!selectedCluster || !selectedVM}
                                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    Aprovar e Atribuir VM
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* VM Request Configuration Modal */}
            {showConfigModal && (
                <VMRequestConfigModal
                    onClose={() => setShowConfigModal(false)}
                    onSave={() => {
                        setShowConfigModal(false);
                        // Refresh data if needed
                    }}
                />
            )}
        </div>
    );
}
// All VMs Tab Component
function AllVMsTab({ allVMs, users, onAssignVM, onUnassignVM, onConsoleAccess }) {
    const [selectedVM, setSelectedVM] = useState(null);
    const [selectedUser, setSelectedUser] = useState('');
    const [sendToBilling, setSendToBilling] = useState(false);
    const [price, setPrice] = useState('');
    const [observations, setObservations] = useState('');

    const [showAssignModal, setShowAssignModal] = useState(false);

    const handleAssignClick = async (vm) => {
        setSelectedVM(vm);
        setSelectedUser('');
        setSendToBilling(false);
        setPrice('');
        setObservations('');
        setShowAssignModal(true);
    };

    const confirmAssign = () => {
        if (!selectedUser) {
            alert('Por favor, selecione um usuário');
            return;
        }

        if (!selectedVM.realNodeName) {
            alert('Erro: Não foi possível identificar o nó real da VM');
            return;
        }

        onAssignVM(
            selectedVM.vmid, 
            selectedVM.nodeId, 
            selectedUser, 
            selectedVM.realNodeName,
            sendToBilling,
            sendToBilling ? price : null,
            sendToBilling ? observations : null
        );
        setShowAssignModal(false);
        setSelectedVM(null);
        setSelectedUser('');
        setSendToBilling(false);
        setPrice('');
        setObservations('');
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'running': return 'bg-green-100 text-green-800';
            case 'stopped': return 'bg-red-100 text-red-800';
            case 'paused': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'running': return 'Ligada';
            case 'stopped': return 'Desligada';
            case 'paused': return 'Pausada';
            default: return 'Desconhecido';
        }
    };

    // Separar VMs atribuídas e não atribuídas
    const assignedVMs = allVMs.filter(vm => vm.assigned);
    const unassignedVMs = allVMs.filter(vm => !vm.assigned);

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Todas as VMs do Proxmox</h2>
                <div className="text-sm text-gray-600">
                    Total: {allVMs.length} VMs | Atribuídas: {assignedVMs.length} | Não atribuídas: {unassignedVMs.length}
                </div>
            </div>

            {/* VMs Não Atribuídas */}
            <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <AlertCircle className="h-5 w-5 text-orange-500 mr-2" />
                    VMs Não Atribuídas ({unassignedVMs.length})
                </h3>

                {unassignedVMs.length === 0 ? (
                    <div className="text-center py-8 bg-white rounded-lg shadow">
                        <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Todas as VMs estão atribuídas</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Não há VMs sem atribuição no momento.
                        </p>
                    </div>
                ) : (
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                        <ul className="divide-y divide-gray-200">
                            {unassignedVMs.map((vm) => (
                                <li key={`${vm.nodeId}-${vm.vmid}`} className="px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-lg font-medium text-gray-900">
                                                    {vm.name ? `${vm.name} (${vm.node}) (ID ${vm.vmid})` : `${vm.node} - VM ${vm.vmid}`}
                                                    <span className={`inline-flex ml-3 items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vm.status)}`}>
                                                        {getStatusText(vm.status)}
                                                    </span>
                                                </h3>
                                            </div>
                                            <div className="mt-2 grid grid-cols-3 gap-4 text-sm text-gray-600">
                                                <div>VM ID: {vm.vmid}</div>
                                                <div>Região: {vm.clusterName}</div>
                                                <div>Nó: {vm.realNodeName}</div>
                                                <div>Host: {vm.clusterHost}</div>
                                                <div>Tipo: {vm.type || 'qemu'}</div>
                                                <div>Status do Nó: <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${vm.realNodeStatus === 'online' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {vm.realNodeStatus || 'unknown'}
                                                </span></div>
                                            </div>
                                            {vm.maxcpu && (
                                                <div className="mt-2 text-sm text-gray-600">
                                                    CPU: {vm.maxcpu} cores | RAM: {Math.round((vm.maxmem || 0) / 1024 / 1024 / 1024)}GB | Disco: {Math.round((vm.maxdisk || 0) / 1024 / 1024 / 1024)}GB
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex space-x-2 ml-4">
                                            <button
                                                onClick={() => onConsoleAccess(vm)}
                                                className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                                            >
                                                <Terminal className="h-4 w-4 mr-1" />
                                                Console
                                            </button>
                                            <button
                                                onClick={() => handleAssignClick(vm)}
                                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                            >
                                                <Users className="h-4 w-4 mr-1" />
                                                Atribuir
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* VMs Atribuídas */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    VMs Atribuídas ({assignedVMs.length})
                </h3>

                {assignedVMs.length === 0 ? (
                    <div className="text-center py-8 bg-white rounded-lg shadow">
                        <Server className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma VM atribuída</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            As VMs atribuídas aparecerão aqui.
                        </p>
                    </div>
                ) : (
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                        <ul className="divide-y divide-gray-200">
                            {assignedVMs.map((vm) => (
                                <li key={`${vm.nodeId}-${vm.vmid}`} className="px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-lg font-medium text-gray-900">
                                                    {vm.name ? `${vm.name} (${vm.node}) (ID ${vm.vmid})` : `${vm.node} - VM ${vm.vmid}`}
                                                    <span className={`inline-flex ml-3 items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vm.status)}`}>
                                                        {getStatusText(vm.status)}
                                                    </span>
                                                </h3>
                                            </div>
                                            <div className="mt-2 grid grid-cols-3 gap-4 text-sm text-gray-600">
                                                <div>VM ID: {vm.vmid}</div>
                                                <div>Região: {vm.clusterName}</div>
                                                <div>Nó: {vm.realNodeName}</div>
                                                <div>Host: {vm.clusterHost}</div>
                                                <div>Status do Nó: <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${vm.realNodeStatus === 'online' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {vm.realNodeStatus || 'unknown'}
                                                </span></div>
                                                <div className="text-green-600 font-medium">✓ Atribuída</div>
                                            </div>
                                            {vm.maxcpu && (
                                                <div className="mt-2 text-sm text-gray-600">
                                                    CPU: {vm.maxcpu} cores | RAM: {Math.round((vm.maxmem || 0) / 1024 / 1024 / 1024)}GB | Disco: {Math.round((vm.maxdisk || 0) / 1024 / 1024 / 1024)}GB
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex space-x-2 ml-4">
                                            <button
                                                onClick={() => onConsoleAccess(vm)}
                                                className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                                            >
                                                <Terminal className="h-4 w-4 mr-1" />
                                                Console
                                            </button>
                                            <button
                                                onClick={() => onUnassignVM(vm.vmid, vm.nodeId)}
                                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                                            >
                                                <XCircle className="h-4 w-4 mr-1" />
                                                Desvincular
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Assign VM Modal */}
            {showAssignModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-10 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white m-4">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Atribuir VM
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                VM: <strong>{selectedVM?.name ? `${selectedVM.name} (ID: ${selectedVM.vmid})` : `VM ${selectedVM.vmid}`}</strong>
                            </p>
                            <p className="text-sm text-gray-600 mb-4">
                                Cluster: <strong>{selectedVM?.clusterName}</strong>
                            </p>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Selecionar Usuário
                                </label>
                                <select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Selecione um usuário</option>
                                    {users.filter(u => u.role === 'client').map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.full_name} [{user.email}]
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {selectedVM?.realNodeName && (
                                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                                    <p className="text-sm text-gray-700">
                                        <strong>Nó Real:</strong> {selectedVM.realNodeName}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        A VM será atribuída automaticamente no nó onde está executando
                                    </p>
                                </div>
                            )}

                            {/* Seção de Faturamento */}
                            <div className="mb-4 bg-gray-50 p-4 rounded-lg border">
                                <div className="flex items-center mb-3">
                                    <input
                                        type="checkbox"
                                        checked={sendToBilling}
                                        onChange={(e) => setSendToBilling(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label className="ml-2 block text-sm font-medium text-gray-700">
                                        Enviar ao financeiro?
                                    </label>
                                </div>
                                
                                {sendToBilling && (
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Valor (R$)</label>
                                            <input
                                                type="number"
                                                value={price}
                                                onChange={(e) => setPrice(e.target.value)}
                                                step="0.01"
                                                min="0"
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="0,00"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Observações (opcional)</label>
                                            <textarea
                                                value={observations}
                                                onChange={(e) => setObservations(e.target.value)}
                                                rows={3}
                                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Observações adicionais sobre a VM..."
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowAssignModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmAssign}
                                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    Atribuir VM
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}